import {
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import dayjs, { Dayjs } from 'dayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  BbLbPhase,
  bbLbCreatePhase,
  bbLbDeletePhase,
  bbLbExportRanking,
  bbLbUpdatePhase,
} from '../../../services/blindBoxService';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import { STATUS_META, downloadRankingXlsx, toDayjs } from './helpers';

type PhaseForm = {
  key: string;
  id?: string; // undefined for the unsaved draft row
  name: string;
  start: Dayjs | null;
  end: Dayjs | null;
  status: string; // '' for the draft
  frozenAt?: string | number | null;
};

const DRAFT_KEY = '__draft__';

const toForm = (p: BbLbPhase): PhaseForm => ({
  key: p.id,
  id: p.id,
  name: p.name ?? '',
  start: toDayjs(p.startTime),
  end: toDayjs(p.endTime),
  status: p.status,
  frozenAt: p.frozenAt ?? null,
});

const fmtDateTime = (d: Dayjs | null) => (d ? d.format('YYYY/MM/DD HH:mm:ss') : '—');

const sameInstant = (d: Dayjs | null, v?: string | number | null) => {
  const o = toDayjs(v);
  if (!d && !o) return true;
  if (!d || !o) return false;
  return d.isSame(o);
};

/** The later of two optional Dayjs values (for lower-bound constraints). */
const laterOf = (a: Dayjs | null, b: Dayjs | null): Dayjs | undefined => {
  if (a && b) return a.isAfter(b) ? a : b;
  return a ?? b ?? undefined;
};

export function PhaseTab({ phases, reload }: { phases: BbLbPhase[]; reload: () => void }) {
  const t = useTranslate();
  const notify = useNotify();
  const [rows, setRows] = useState<PhaseForm[]>(() => phases.map(toForm));
  const [draft, setDraft] = useState<PhaseForm | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);

  // Keep the editable rows in sync with the server list (the draft is independent).
  useEffect(() => {
    setRows(phases.map(toForm));
  }, [phases]);

  const newest = phases[0]; // list is newest-first; a new phase must follow it
  const prevStart = toDayjs(newest?.startTime);
  const prevEnd = toDayjs(newest?.endTime);

  const startCreate = () => {
    if (draft) return;
    // Seed the draft to the day after the newest phase ends (00:00) with a 7-day window (…23:59);
    // the time is editable — these are just defaults that satisfy the "after previous" rule.
    const seedStart = prevEnd ? prevEnd.add(1, 'day').startOf('day') : null;
    const seedEnd = seedStart ? seedStart.add(6, 'day').endOf('day') : null;
    setDraft({
      key: DRAFT_KEY,
      name: `Phase ${phases.length + 1}`,
      start: seedStart,
      end: seedEnd,
      status: '',
      frozenAt: null,
    });
  };

  const setDraftField = (patch: Partial<PhaseForm>) =>
    setDraft((d) => (d ? { ...d, ...patch } : d));
  const setRowField = (key: string, patch: Partial<PhaseForm>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  /** Returns an i18n error key or null. `checkPrev` enforces "after the previous phase". */
  const validate = (f: PhaseForm, checkPrev: boolean): string | null => {
    if (!f.name.trim()) return 'module.blindBox.lbErrPhaseName';
    if (!f.start || !f.end) return 'module.blindBox.lbErrTimeRequired';
    if (!f.end.isAfter(f.start)) return 'module.blindBox.lbErrEndAfterStart';
    if (checkPrev) {
      if ((prevStart && !f.start.isAfter(prevStart)) || (prevEnd && !f.end.isAfter(prevEnd))) {
        return 'module.blindBox.lbErrAfterPrev';
      }
    }
    return null;
  };

  const saveDraft = async () => {
    if (!draft) return;
    const err = validate(draft, true);
    if (err) {
      notify(t(err), { type: 'warning' });
      return;
    }
    setSavingKey(DRAFT_KEY);
    try {
      await bbLbCreatePhase({
        name: draft.name.trim(),
        startTime: draft.start!.toISOString(),
        endTime: draft.end!.toISOString(),
      });
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setDraft(null);
      reload();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setSavingKey(null);
    }
  };

  const saveRow = async (form: PhaseForm) => {
    if (!form.id) return;
    const err = validate(form, false);
    if (err) {
      notify(t(err), { type: 'warning' });
      return;
    }
    setSavingKey(form.key);
    try {
      // A running phase may only edit its end time — send endTime alone.
      await bbLbUpdatePhase(
        form.id,
        form.status === 'active'
          ? { endTime: form.end!.toISOString() }
          : {
              name: form.name.trim(),
              startTime: form.start!.toISOString(),
              endTime: form.end!.toISOString(),
            }
      );
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      reload();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setSavingKey(null);
    }
  };

  const resetRow = (form: PhaseForm) => {
    const src = phases.find((p) => p.id === form.id);
    if (src) setRows((prev) => prev.map((r) => (r.key === form.key ? toForm(src) : r)));
  };

  const delRow = async (form: PhaseForm) => {
    if (form.status !== 'upcoming' || !form.id) {
      notify(t('module.blindBox.lbPhaseLockedHint'), { type: 'warning' });
      return;
    }
    if (!window.confirm(t('module.blindBox.confirmDeletePhase'))) return;
    setSavingKey(form.key);
    try {
      await bbLbDeletePhase(form.id);
      notify(t('module.blindBox.deleteOk'), { type: 'success' });
      reload();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setSavingKey(null);
    }
  };

  const exportRanking = async (form: PhaseForm) => {
    if (!form.id) return;
    setExportingId(form.id);
    try {
      const data = await bbLbExportRanking(form.id);
      if (!data.boards?.length) {
        notify(t('module.blindBox.lbNoRanking'), { type: 'info' });
        return;
      }
      await downloadRankingXlsx(
        `leaderboard-ranking-${form.name || form.id}-${dayjs().format('YYYYMMDD-HHmmss')}.xlsx`,
        data
      );
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setExportingId(null);
    }
  };

  const renderRow = (form: PhaseForm) => {
    const isDraft = !form.id;
    // Draft & upcoming phases are fully editable; a running (active) phase may edit endTime only.
    const fullEdit = isDraft || form.status === 'upcoming';
    const endEdit = fullEdit || form.status === 'active';
    const src = phases.find((p) => p.id === form.id);
    const dirty =
      !isDraft &&
      !!src &&
      (form.name !== (src.name ?? '') ||
        !sameInstant(form.start, src.startTime) ||
        !sameInstant(form.end, src.endTime));
    const busy = savingKey === form.key;
    const update = (patch: Partial<PhaseForm>) =>
      isDraft ? setDraftField(patch) : setRowField(form.key, patch);
    const meta = STATUS_META[form.status];

    return (
      <TableRow key={form.key} hover>
        <TableCell sx={{ height: 50 }}>
          {fullEdit ? (
            <TextField
              size="small"
              variant="standard"
              value={form.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder={t('module.blindBox.lbPhaseName')}
              fullWidth
              sx={{ '& .MuiInputBase-root': { minHeight: 30 } }}
            />
          ) : (
            form.name || '—'
          )}
        </TableCell>
        <TableCell>
          {fullEdit ? (
            <DateTimePicker
              value={form.start}
              onChange={(v) => update({ start: v })}
              views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
              minDateTime={isDraft ? (prevStart ?? undefined) : undefined}
              maxDateTime={form.end ?? undefined}
              format="YYYY/MM/DD HH:mm:ss"
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'standard',
                  sx: { width: 190, '& .MuiInputBase-root': { minHeight: 30 } },
                },
              }}
            />
          ) : (
            <span style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(form.start)}</span>
          )}
        </TableCell>
        <TableCell>
          {endEdit ? (
            <DateTimePicker
              value={form.end}
              onChange={(v) => update({ end: v })}
              views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
              minDateTime={isDraft ? laterOf(form.start, prevEnd) : (form.start ?? undefined)}
              format="YYYY/MM/DD HH:mm:ss"
              slotProps={{
                textField: {
                  size: 'small',
                  variant: 'standard',
                  sx: {
                    width: 190,
                    '& .MuiInputBase-root': {
                      minHeight: 30,
                    },
                  },
                },
              }}
            />
          ) : (
            <span style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(form.end)}</span>
          )}
        </TableCell>
        <TableCell>
          {isDraft ? (
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label={t('module.blindBox.lbDraft')}
            />
          ) : (
            <Chip
              size="small"
              color={meta?.color ?? 'default'}
              label={meta ? t(meta.key) : form.status}
            />
          )}
        </TableCell>
        {/* <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDay(toDayjs(form.frozenAt))}</TableCell> */}
        <TableCell align="right">
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            {isDraft ? (
              <>
                <PermissionButton resource="bb-leaderboard" action="create">
                  <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    disabled={busy}
                    onClick={saveDraft}
                  >
                    {t('actions.save')}
                  </Button>
                </PermissionButton>
                <Button size="small" variant="outlined" onClick={() => setDraft(null)}>
                  {t('actions.cancel')}
                </Button>
              </>
            ) : (
              <>
                {endEdit && dirty ? (
                  <>
                    <PermissionButton resource="bb-leaderboard" action="update">
                      <Button
                        size="small"
                        variant="contained"
                        color="secondary"
                        disabled={busy}
                        onClick={() => saveRow(form)}
                      >
                        {t('actions.save')}
                      </Button>
                    </PermissionButton>
                    <Button size="small" variant="outlined" onClick={() => resetRow(form)}>
                      {t('common.reset', { _: 'Reset' })}
                    </Button>
                  </>
                ) : null}
                {form.status === 'upcoming' ? (
                  <PermissionButton resource="bb-leaderboard" action="delete">
                    <Tooltip title={t('actions.delete')} arrow>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={busy}
                          onClick={() => delRow(form)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </PermissionButton>
                ) : null}
                {(form.status === 'completed' || form.status === 'active') && (
                  <Tooltip title={t('module.blindBox.lbExportRanking')} arrow>
                    <span>
                      <IconButton
                        size="small"
                        disabled={exportingId === form.id}
                        onClick={() => exportRanking(form)}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </>
            )}
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Stack spacing={2}>
      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('module.blindBox.lbPhaseName')}</TableCell>
              <TableCell>{t('module.blindBox.lbStartTime')}</TableCell>
              <TableCell>{t('module.blindBox.lbEndTime')}</TableCell>
              <TableCell>{t('module.blindBox.lbStatus')}</TableCell>
              {/* <TableCell>{t('module.blindBox.lbFrozenAt')}</TableCell> */}
              <TableCell align="right">{t('module.blindBox.mdColOperate')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell colSpan={6} sx={{ py: 1.5 }}>
                <PermissionButton resource="bb-leaderboard" action="create">
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    disabled={!!draft}
                    onClick={startCreate}
                  >
                    {t('module.blindBox.lbCreatePhase')}
                  </Button>
                </PermissionButton>
              </TableCell>
            </TableRow>
            {draft ? renderRow(draft) : null}
            {rows.length === 0 && !draft ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('module.blindBox.lbNoPhases')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => renderRow(r))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
